/**
 * @module ReasoningEmptyState
 * @purpose Empty state component for the reasoning panel — no clusters surfaced.
 * @depends react-i18next
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display — no data fetching
 * - State: none
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'

/**
 * Renders the reasoning panel empty state with a link to refresh QC scope.
 *
 * @behaviour Text sourced from analysis.reasoning.empty.* namespace.
 *            Refresh QC link rendered as a styled button for accessibility.
 * @errors n/a — purely presentational
 */
interface ReasoningEmptyStateProps {
  /** Starts a reasoning run. Absent when the caller cannot trigger one. */
  onRunReasoning?: () => void
}

export function ReasoningEmptyState({
  onRunReasoning,
}: ReasoningEmptyStateProps = {}): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-8">
      <p className="text-sm" style={{ color: 'var(--text)' }}>
        {t('reasoning.empty.title')}
      </p>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {t('reasoning.empty.subtitle')}
      </p>
      {onRunReasoning !== undefined && (
        <button
          type="button"
          onClick={onRunReasoning}
          className="mt-1 text-xs uppercase tracking-wide underline hover:no-underline"
          style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}
        >
          {t('reasoning.empty.runReasoning')}
        </button>
      )}
    </div>
  )
}
